import { useState, useEffect, useRef } from 'react';
import Plot from 'react-plotly.js';
import { useAuth } from '../../context/AuthContext';
import { get, API_URL } from '../../utils/api';
import SegmentedControl from '../../components/common/SegmentedControl';

// API URL is now imported from api.js

function UserUsageData() {
  const { user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [powerData, setPowerData] = useState(null);
  const [energyData, setEnergyData] = useState(null);
  const [selectedDate, setSelectedDate] = useState(
   // new Date().toISOString().split('T')[0] // Today's date in YYYY-MM-DD format
    (() => {
      // Get yesterday's date in YYYY-MM-DD format
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      return yesterday.toISOString().split('T')[0];
    })()
  );
  const [dataType, setDataType] = useState('power'); // 'power' or 'energy'
  const [debugMode, setDebugMode] = useState(false); // For using test data
  const [usingCachedData, setUsingCachedData] = useState(false); // Track if we're using cached data
  
  // Test UUID with data for development
  const TEST_UUID = "af098471-dd3c-49ce-ad09-577c04d26e64";

  // Earliest date the user can navigate to (30 days ago)
  const MAX_PAST_DATE = (() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  })();
  
  const chartRef = useRef(null);

  // Helper function to get cache key for data
  const getCacheKey = (dataType, date, uuid) => {
    return `electricity_data_${dataType}_${date}_${uuid}`;
  };
  
  // Helper function to check if cached data is still valid (less than 15 minutes old)
  const isCacheValid = (timestamp) => {
    const fifteenMinutesInMs = 15 * 60 * 1000;
    return Date.now() - timestamp < fifteenMinutesInMs;
  };

  // Function to fetch power data from the API with caching
  const fetchPowerData = async (date) => {
    setLoading(true);
    setError(null);
    
    try {
      // Use test UUID if in debug mode, otherwise use the actual user ACCOUNT UUID
      const uuid = debugMode ? TEST_UUID : user?.accountUuid;
      
      if (!uuid) {
        throw new Error('User Account ID not available');
      }
      
      // Check if we have this data in cache
      const cacheKey = getCacheKey('power', date, uuid);
      const cachedData = sessionStorage.getItem(cacheKey);
      
      if (cachedData) {
        try {
          const parsedCache = JSON.parse(cachedData);
          if (isCacheValid(parsedCache.timestamp)) {
            // console.log('Using cached power data for:', date);
            setPowerData(parsedCache.data);
            setLoading(false);
            setUsingCachedData(true);
            return;
          } else {
            // console.log('Cached power data expired for:', date);
          }
        } catch (e) {
          // console.error('Error parsing cached data:', e);
          // Invalid cache, continue with API request
        }
      }
      
      // If no valid cache, fetch from API
      // console.log('Fetching fresh power data for:', date);
      const response = await get(`${API_URL}/data/power`, {
        date,
        uuid
      });

      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        // Save the valid data
        const dataToCache = {
          data: response.data[0],
          timestamp: Date.now()
        };
        
        // Update state
        setPowerData(response.data[0]);
        
        // Save to cache
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify(dataToCache));
        } catch (e) {
          // console.error('Error saving to cache:', e);
          // Continue even if caching fails
        }
      } else {
        throw new Error('Invalid data format received from API');
      }
    } catch (err) {
      // console.error('Error fetching power data:', err);
      setError('Failed to load power data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch energy data from the API with caching
  const fetchEnergyData = async (date) => {
    setLoading(true);
    setError(null);
    
    try {
      // Use test UUID if in debug mode, otherwise use the actual user ACCOUNT UUID
      const uuid = debugMode ? TEST_UUID : user?.accountUuid;
      
      if (!uuid) {
        throw new Error('User Account ID not available');
      }
      
      // Check if we have this data in cache
      const cacheKey = getCacheKey('energy', date, uuid);
      const cachedData = sessionStorage.getItem(cacheKey);
      
      if (cachedData) {
        try {
          const parsedCache = JSON.parse(cachedData);
          if (isCacheValid(parsedCache.timestamp)) {
            // console.log('Using cached energy data for:', date);
            setEnergyData(parsedCache.data);
            setLoading(false);
            setUsingCachedData(true);
            return;
          } else {
            // console.log('Cached energy data expired for:', date);
          }
        } catch (e) {
          // console.error('Error parsing cached data:', e);
          // Invalid cache, continue with API request
        }
      }
      
      // If no valid cache, fetch from API
      // console.log('Fetching fresh energy data for:', date);
      const response = await get(`${API_URL}/data/energy`, {
        date,
        uuid
      });

      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        // Save the valid data
        const dataToCache = {
          data: response.data[0],
          timestamp: Date.now()
        };
        
        // Update state
        setEnergyData(response.data[0]);
        
        // Save to cache
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify(dataToCache));
        } catch (e) {
          // console.error('Error saving to cache:', e);
          // Continue even if caching fails
        }
      } else {
        throw new Error('Invalid data format received from API');
      }
    } catch (err) {
      // console.error('Error fetching energy data:', err);
      setError('Failed to load energy data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Toggle debug mode
  const toggleDebugMode = () => {
    setDebugMode(prev => !prev);
  };

  // Fetch data when date, data type, or debug mode changes
  useEffect(() => {
    if (isAuthenticated) {
      if (dataType === 'power') {
        fetchPowerData(selectedDate);
      } else {
        fetchEnergyData(selectedDate);
      }
    } else {
      // If not authenticated, show an error
      setError("You need to be logged in to view your electricity usage data.");
      setLoading(false);
    }
  }, [selectedDate, dataType, isAuthenticated, user?.accountUuid, debugMode]);

  // Check if a date is in the future
  const isFutureDate = (dateStr) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const compareDate = new Date(dateStr);
    return compareDate > today;
  };
  
  // Check if a date is before our max past date
  const isBeforeMaxPastDate = (dateStr) => {
    const maxPastDate = new Date(MAX_PAST_DATE);
    const compareDate = new Date(dateStr);
    return compareDate < maxPastDate;
  };

  const handleDateChange = (e) => {
    const newDate = e.target.value;
    
    if (isFutureDate(newDate)) {
      setError("Cannot select future dates");
      return;
    }
    
    if (isBeforeMaxPastDate(newDate)) {
      setError("Cannot select dates older than 30 days");
      return;
    }
    
    setSelectedDate(newDate);
  };
  
  const goToToday = () => {
    setSelectedDate(new Date().toISOString().split('T')[0]);
  };
  
  const goToPreviousDay = () => {
    const currentDate = new Date(selectedDate);
    currentDate.setDate(currentDate.getDate() - 1);
    const newDate = currentDate.toISOString().split('T')[0];
    
    if (isBeforeMaxPastDate(newDate)) {
      setError("Cannot go further than 30 days in the past");
      return;
    }
    
    setSelectedDate(newDate);
  };
  
  const goToNextDay = () => {
    const currentDate = new Date(selectedDate);
    currentDate.setDate(currentDate.getDate() + 1);
    const newDate = currentDate.toISOString().split('T')[0];
    
    if (isFutureDate(newDate)) {
      setError("Cannot select future dates");
      return;
    }
    
    setSelectedDate(newDate);
  };
  
  // Toggle between power and energy data views
  const toggleDataType = () => {
    setDataType(prevType => prevType === 'power' ? 'energy' : 'power');
  };

  // Prepare data for the plot
  const preparePlotData = () => {
    const currentData = dataType === 'power' ? powerData : energyData;
    if (!currentData || !currentData.data || !Array.isArray(currentData.data)) return null;
    
    // Process API data to ensure 5-minute intervals
    const processedData = processApiData(currentData.data);
    
    // Create the plot with the processed data
    createPlot(processedData);
    
    return true;
  };

  // Calculate the average value
  const calculateAverage = () => {
    const currentData = dataType === 'power' ? powerData : energyData;
    if (!currentData || !currentData.data || !Array.isArray(currentData.data) || currentData.data.length === 0) return 0;
    
    // For power data, we calculate the average of all readings
    if (dataType === 'power') {
      const sum = currentData.data.reduce((total, item) => total + parseFloat(item.y || 0), 0);
      return (sum / currentData.data.length).toFixed(2);
    } 
    // For energy data, we don't calculate average since it's cumulative
    else {
      return 'N/A';
    }
  };

  // Calculate the peak value for power or total consumption for energy
  const calculatePeak = () => {
    const currentData = dataType === 'power' ? powerData : energyData;
    if (!currentData || !currentData.data || !Array.isArray(currentData.data) || currentData.data.length === 0) return 0;
    
    if (dataType === 'power') {
      // For power, we want the peak value
      const peak = Math.max(...currentData.data.map(item => parseFloat(item.y || 0)));
      return peak.toFixed(2);
    } else {
      // For energy, we want the last value (total consumption)
      const sortedData = [...currentData.data].sort((a, b) => {
        const timeA = a.x.split(':').map(Number);
        const timeB = b.x.split(':').map(Number);
        if (timeA[0] !== timeB[0]) return timeA[0] - timeB[0];
        return timeA[1] - timeB[1];
      });
      
      const lastValue = sortedData[sortedData.length - 1]?.y || 0;
      return parseFloat(lastValue).toFixed(2);
    }
  };

  // Generate time grid with 5-minute intervals from 00:00 to 23:55
  function generateTimeGrid() {
    const timeGrid = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 5) {
        const hourStr = hour.toString().padStart(2, '0');
        const minuteStr = minute.toString().padStart(2, '0');
        timeGrid.push(`${hourStr}:${minuteStr}`);
      }
    }
    return timeGrid;
  }

  // Process API data to snap to 5-minute intervals
  function processApiData(apiData) {
    const timeGrid = generateTimeGrid();
    
    // If today's date is selected, limit the grid to current time
    const isToday = selectedDate === new Date().toISOString().split('T')[0];
    let limitedTimeGrid = timeGrid;
    
    if (isToday) {
      const now = new Date();
      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();
      // Round to nearest 5 minutes
      const roundedMinute = Math.floor(currentMinute / 5) * 5;
      const currentTimeStr = `${currentHour.toString().padStart(2, '0')}:${roundedMinute.toString().padStart(2, '0')}`;
      
      // Filter time grid to only include times up to current time
      limitedTimeGrid = timeGrid.filter(time => {
        const [h, m] = time.split(':').map(Number);
        const [ch, cm] = currentTimeStr.split(':').map(Number);
        return h < ch || (h === ch && m <= cm);
      });
    }
    
    const gridData = Array(limitedTimeGrid.length).fill(0);
    const dataPointCounts = Array(limitedTimeGrid.length).fill(0);
    
    // Process each data point from the API
    apiData.forEach(point => {
      // Parse the time (assuming format like "10:37")
      const [hours, minutes] = point.x.split(':').map(Number);
      
      // Calculate nearest 5-minute interval
      const roundedMinutes = Math.round(minutes / 5) * 5;
      const adjustedHours = hours + (roundedMinutes === 60 ? 1 : 0);
      const adjustedMinutes = roundedMinutes % 60;
      
      // Format back to string
      const roundedHour = (adjustedHours % 24).toString().padStart(2, '0');
      const roundedMinute = adjustedMinutes.toString().padStart(2, '0');
      const roundedTime = `${roundedHour}:${roundedMinute}`;
      
      // Find the index in our grid
      const gridIndex = limitedTimeGrid.indexOf(roundedTime);
      if (gridIndex !== -1) {
        gridData[gridIndex] += parseFloat(point.y || 0);
        dataPointCounts[gridIndex]++;
      }
    });
    
    // Calculate average if multiple points map to the same interval
    const resultData = limitedTimeGrid.map((time, index) => {
      return {
        x: time,
        y: dataPointCounts[index] > 0 ? (gridData[index] / dataPointCounts[index]) : 0
      };
    });
    
    return resultData;
  }

  // Create the plot with the processed data
  function createPlot(processedData) {
    const timeGrid = generateTimeGrid();
    
    // If today's date is selected, adjust the time range to show only up to current time
    const isToday = selectedDate === new Date().toISOString().split('T')[0];
    let xaxisRange = ['00:00', '23:55'];
    
    if (isToday && processedData.length > 0) {
      // Use the last data point time as the end of the range
      const lastTime = processedData[processedData.length - 1].x;
      xaxisRange = ['00:00', lastTime];
    }

    // Format the selected date for display in the chart title
    const formattedDate = new Date(selectedDate).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
    
    // Axis font size
    const axisFontSize = 16;
    
    // Define plot data with improved styling based on data type
    const plotData = [{
      x: processedData.map(point => point.x),
      y: processedData.map(point => point.y),
      type: 'scatter',
      mode: 'lines',
      name: dataType === 'power' ? 'Active Power' : 'Electricity Consumption',
      line: { 
        color: dataType === 'power' ? 'rgba(255, 111, 104, 1)' : 'rgba(100, 149, 237, 1)', 
        width: 3
      },
      fill: 'tozeroy',
      fillcolor: dataType === 'power' ? 'rgba(255, 130, 120, 0.2)' : 'rgba(115, 157, 241, 0.2)',
      hovertemplate: dataType === 'power' ? '%{y:.2f} kW' : '%{y:.2f} kWh'
    }];
    
    // Improved layout configuration
    const layout = {
      title: {
        text: dataType === 'power' 
          ? `Daily Active Consumption (kW) on ${formattedDate}` 
          : `Daily Electricity Consumption (kWh) on ${formattedDate}`,
        font: {
          family: 'Roboto, "Helvetica Neue", Arial, sans-serif',
          size: 20,
          color: '#000000'
        }
      },
      xaxis: {
        title: {
          text: 'Time of Day',
          font: {
            family: 'Roboto, "Helvetica Neue", Arial, sans-serif',
            size: axisFontSize,
            color: '#000000',
            weight: 'bold'
          }
        },
        tickmode: 'array',
        tickvals: timeGrid.filter((_, index) => index % 12 === 0),
        ticktext: timeGrid.filter((_, index) => index % 12 === 0),
        tickangle: -45,
        range: xaxisRange,
        tickformat: '%H:%M',
        tickfont: {
          family: 'Roboto, "Helvetica Neue", Arial, sans-serif',
          color: '#000000'
        }
      },
      yaxis: {
        title: {
          text: dataType === 'power' ? 'Active Power (kW)' : 'Electricity Consumption (kWh)',
          font: {
            family: 'Roboto, "Helvetica Neue", Arial, sans-serif',
            size: axisFontSize,
            color: '#000000',
            weight: 'bold'
          },
          standoff: 30  // Increased spacing between y-axis title and axis
        },
        side: 'left',
        fixedrange: true,
        rangemode: 'tozero',
        tickfont: {
          family: 'Roboto, "Helvetica Neue", Arial, sans-serif',
          color: '#000000'
        }
      },
      margin: { 
        l: 90,  // Further increased left margin for y-axis label
        r: 50, 
        b: 100, 
        t: 100,
        pad: 4
      },
      // Improve the modebar appearance
      modebar: {
        orientation: 'h',  // horizontal orientation
        bgcolor: 'rgba(255,255,255,0.7)',
        color: '#000000',
        activecolor: '#3498db'
      },
      // Set the plot background color to white
      plot_bgcolor: 'white',
      paper_bgcolor: 'white'
    };
    
    // Configuration options
    const config = {
      modeBarButtonsToAdd: [
        'downloadImage'
      ],
      modeBarButtonsToRemove: [
        'lasso2d',
        'select2d',
        'autoScale2d'
      ],
      toImageButtonOptions: {
        format: 'png',
        filename: dataType === 'power' 
          ? `active_power_chart_${selectedDate}` 
          : `consumption_chart_${selectedDate}`,
        height: 500,
        width: 700,
        scale: 1
      },
      displaylogo: false,
      responsive: true,
      displayModeBar: 'hover',  // Only show on hover
      showAxisDragHandles: false
    };
    
    // Render the plot
    Plotly.newPlot('chart', plotData, layout, config);
  }

  // Render component
  useEffect(() => {
    if (!loading && !error) {
      preparePlotData();
      
      // Fix the modebar position after plot is rendered
      const fixModeBar = () => {
        const modeBar = document.querySelector('.modebar');
        if (modeBar) {
          // Position it at the top right
          modeBar.style.position = 'absolute';
          modeBar.style.top = '10px';
          modeBar.style.right = '10px';
          modeBar.style.left = 'auto';
          modeBar.style.transform = 'none';
          // Make sure it's horizontal
          modeBar.style.display = 'flex';
          modeBar.style.flexDirection = 'row';
          // console.log('Fixed modebar position');
        }
      };
      
      // Try to fix after the plot has time to render
      setTimeout(fixModeBar, 100);
      setTimeout(fixModeBar, 500);
      setTimeout(fixModeBar, 1000);
    }
  }, [loading, error, dataType, powerData, energyData]);

  // Cache management functions
  const clearAllCache = () => {
    // Clear only our app's cached electricity data
    Object.keys(sessionStorage).forEach(key => {
      if (key.startsWith('electricity_data_')) {
        sessionStorage.removeItem(key);
      }
    });
    // console.log('Cleared all electricity data from cache');
  };
  
  const clearCurrentDateCache = () => {
    const uuid = debugMode ? TEST_UUID : user?.accountUuid;
    if (!uuid) return;
    
    const powerKey = getCacheKey('power', selectedDate, uuid);
    const energyKey = getCacheKey('energy', selectedDate, uuid);
    
    sessionStorage.removeItem(powerKey);
    sessionStorage.removeItem(energyKey);
    
    // console.log(`Cleared cache for ${selectedDate}`);
  };
  
  // Force refresh current data
  const refreshCurrentData = () => {
    // First clear the cache for this date
    clearCurrentDateCache();
    
    // Then refetch
    setUsingCachedData(false);
    if (dataType === 'power') {
      fetchPowerData(selectedDate);
    } else {
      fetchEnergyData(selectedDate);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Add CSS to fix modebar position */}
      <style>
        {`
          .modebar {
            position: absolute !important;
            top: 10px !important;
            right: 10px !important;
            left: auto !important;
            transform: none !important;
            display: flex !important;
            flex-direction: row !important;
          }
        `}
      </style>
    
      <style>
        {`
          .js-plotly-plot .plotly .modebar {
            position: absolute !important;
            top: 10px !important;
            right: 10px !important;
            left: auto !important;
            transform: none !important;
            display: flex !important;
            flex-direction: row !important;
            z-index: 1000 !important;
          }
          .js-plotly-plot .plotly .modebar-group {
            display: flex !important;
            flex-direction: row !important;
            margin-left: 5px !important;
          }
          .js-plotly-plot .plotly .modebar-btn {
            display: inline-block !important;
          }
        `}
      </style>
    
      <h1 className="text-3xl font-bold mb-6 text-hooray-red font-mont">Electricity Data</h1>
      
      <div className="mb-6 bg-blue-50 p-3 rounded border border-blue-200">
        <p className="text-blue-800">
          <strong>Note:</strong> You can view electricity data for up to 30 days in the past. Use the date picker or arrow buttons to navigate.
        </p>
      </div>
      
      <div className="mb-6 flex flex-wrap items-center justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <button 
            onClick={goToPreviousDay}
            className="bg-hooray-red hover:bg-red-700 text-white px-4 py-2 rounded disabled:bg-gray-400 disabled:cursor-not-allowed disabled:opacity-60"
            aria-label="Previous day"
            disabled={isBeforeMaxPastDate((() => { const d = new Date(selectedDate); d.setDate(d.getDate() - 1); return d.toISOString().split('T')[0]; })())}
          >
            ←
          </button>
          
          <input
            type="date"
            value={selectedDate}
            onChange={handleDateChange}
            min={MAX_PAST_DATE}
            max={new Date().toISOString().split('T')[0]}
            className="border border-gray-300 rounded px-3 py-2"
          />
          
          <button 
            onClick={goToNextDay}
            className="bg-hooray-red hover:bg-red-700 text-white px-4 py-2 rounded disabled:bg-gray-400 disabled:cursor-not-allowed disabled:opacity-60"
            aria-label="Next day"
            disabled={isFutureDate((() => { const d = new Date(selectedDate); d.setDate(d.getDate() + 1); return d.toISOString().split('T')[0]; })())}
          >
            →
          </button>
          
          <button 
            onClick={goToToday}
            className="bg-hooray-red hover:bg-red-700 text-white px-4 py-2 rounded ml-2"
          >
            Today
          </button>
          
          {/* Debug toggle for development - temporarily commented out
          <div className="ml-4 flex items-center">
            <label htmlFor="debugToggle" className="flex items-center cursor-pointer">
              <div className="relative">
                <input 
                  type="checkbox" 
                  id="debugToggle" 
                  className="sr-only" 
                  checked={debugMode}
                  onChange={toggleDebugMode}
                />
                <div className={`block w-14 h-8 rounded-full ${debugMode ? 'bg-red-500' : 'bg-gray-400'}`}></div>
                <div className={`dot absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition transform ${debugMode ? 'translate-x-6' : ''}`}></div>
              </div>
              <div className="ml-3 text-sm font-medium">
                {debugMode ? 'Using Test Data' : 'Using My Data'}
              </div>
            </label>
          </div>
          */}
          
          {/* Cache management dropdown - temporarily commented out
          <div className="ml-4 relative group">
            <button 
              className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded flex items-center"
            >
              <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Cache
            </button>
            <div className="hidden group-hover:block absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10">
              <div className="py-1">
                <button 
                  onClick={refreshCurrentData}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Refresh Current Day
                </button>
                <button 
                  onClick={clearCurrentDateCache}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Clear Current Day Cache
                </button>
                <button 
                  onClick={clearAllCache}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Clear All Cached Data
                </button>
                <div className="border-t border-gray-100 my-1"></div>
                <div className="px-4 py-2 text-xs text-gray-500">
                  Cache expires after 15 minutes
                </div>
              </div>
            </div>
          </div>
          */}
        </div>
        
        <div className="mt-4 md:mt-0">
          <SegmentedControl
            options={[
              { label: 'Power (kW)', value: 'power' },
              { label: 'Energy (kWh)', value: 'energy' },
            ]}
            value={dataType}
            onChange={setDataType}
          />
        </div>
      </div>
      
      {/* Debug mode notification - temporarily commented out 
      {debugMode && (
        <div className="mb-4 bg-red-50 p-3 rounded border border-red-200">
          <p className="text-red-800">
            <strong>Development Mode:</strong> Currently viewing test data using account: {TEST_UUID}
          </p>
        </div>
      )}
      */}
      
      {dataType === 'energy' && (
        <div className="mb-4 bg-blue-50 p-3 rounded border border-blue-200">
          <p className="text-blue-800">
            <strong>Note:</strong> This graph shows the <em>cumulative</em> energy consumption throughout the day. 
            The final value represents your total electricity usage for this day.
          </p>
        </div>
      )}
      
      {/* Using cached data notification - temporarily commented out
      {usingCachedData && (
        <div className="mb-4 bg-yellow-50 p-3 rounded border border-yellow-200 flex justify-between items-center">
          <p className="text-yellow-800">
            <strong>Note:</strong> Showing cached data from your previous visit. 
            <span className="ml-1 text-sm">(Cache expires after 15 minutes)</span>
          </p>
          <button 
            onClick={refreshCurrentData}
            className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded flex items-center text-sm"
          >
            <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh Data
          </button>
        </div>
      )}
      */}
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="loader">Loading...</div>
        </div>
      ) : (
        <div id="chartContainer" className="mb-6">
          <div className="w-full h-[500px]">
            <div className="chart-container">
              <div id="chart" ref={chartRef} style={{ width: '100%', height: '100%' }}></div>
            </div>
          </div>
        </div>
      )}
      
      {((dataType === 'power' && powerData) || (dataType === 'energy' && energyData)) && !loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-white p-4 rounded shadow">
            <h3 className="text-lg font-semibold mb-2">
              {dataType === 'power' ? 'Average Power Usage' : 'Average N/A'}
            </h3>
            <p className="text-2xl">{calculateAverage()} {dataType === 'power' ? 'kW' : ''}</p>
            {dataType === 'energy' && (
              <p className="text-sm text-gray-600 mt-1">
                (Not applicable for cumulative data)
              </p>
            )}
          </div>
          <div className="bg-white p-4 rounded shadow">
            <h3 className="text-lg font-semibold mb-2">
              {dataType === 'power' ? 'Peak Power Usage' : 'Total Daily Consumption'}
            </h3>
            <p className="text-2xl">{calculatePeak()} {dataType === 'power' ? 'kW' : 'kWh'}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserUsageData; 